import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card } from "@/ui/components";
import { smsReviewService, type InvoiceSmsPolicy, type SmsReviewItem, type SmsReviewList } from "@/services/smsReviewService";
import "./SmsReviewPage.css";

const reasonText: Record<string, string> = {
  billing_sms_permission_off: "Service SMS permission has not been recorded for this billing contact.",
  billing_contact_permission_changed: "The billing contact changed. Record their SMS permission again.",
  billing_mobile_missing_or_invalid: "The invoice billing contact needs a valid Australian mobile.",
  owner_mobile_missing_or_invalid: "The quotation owner needs a valid Australian notification mobile.",
  quotation_owner_missing: "This quotation has no explicit responsible person.",
  quotation_owner_not_active: "The quotation owner could not be verified as an active company user.",
  quotation_sms_permission_off: "The quotation owner has not opted in to signing SMS notifications.",
  one_segment_limit: "The full source and business reference exceed one SMS segment. No text has been shortened.",
  invoice_paid: "The invoice is paid.",
  invoice_not_issued: "This invoice is no longer eligible for reminders.",
  invoice_disputed_or_paused: "Reminders are paused or this invoice is disputed.",
  stage_30_current: "The 30-day stage replaces this unconfirmed 14-day reminder.",
  reminder_stage_changed: "The invoice is now in a different reminder stage.",
  sms_provider_disabled: "Sending is disabled. A fresh per-item confirmation will be needed before any future send.",
  recipient_unsubscribed: "The provider blocked this recipient's unsubscribe preference.",
  mobilemessage_configuration_missing: "The SMS backend is not configured.",
  preview_changed: "The invoice, contact or final text changed. Review the refreshed preview.",
  user_skipped: "Skipped by a reviewer.",
};
const pendingStates = ["Pending", "Blocked", "Disabled"];
const terminalDescriptions: Record<string, string> = {
  Accepted: "Accepted by Mobile Message. Delivery has not been confirmed.",
  Unknown: "The sending result is unknown. Check the audit/provider record before any further action.",
  Submitting: "Submission has started. Do not retry; check the recorded result.",
  Failed: "Submission failed. This record will not be retried automatically.",
  Skipped: "Skipped. This stage will not be automatically prepared again.",
};
function InvoiceControls({ item, busy, onSave }: { item: SmsReviewItem; busy: boolean; onSave: (policy: InvoiceSmsPolicy) => void }) {
  const initial = item.invoicePolicy || { enabled: false, paused: false, disputed: false, timeZone: null };
  const [policy, setPolicy] = useState<InvoiceSmsPolicy>({ ...initial,
    enabled: initial.enabled && item.snapshot.blockReason !== "billing_contact_permission_changed" });
  return (
    <details className="sms-review-policy">
      <summary>Billing contact SMS permission and reminder controls</summary>
      <fieldset disabled={busy}>
        <legend>{item.snapshot.reference} · this billing contact</legend>
        <label><input type="checkbox" checked={policy.enabled} onChange={event => setPolicy({ ...policy, enabled: event.target.checked })} />
          Billing contact permits service SMS to the displayed mobile</label>
        <label><input type="checkbox" checked={policy.paused} onChange={event => setPolicy({ ...policy, paused: event.target.checked })} />Pause SMS reminders</label>
        <label><input type="checkbox" checked={policy.disputed} onChange={event => setPolicy({ ...policy, disputed: event.target.checked })} />Disputed · suppress SMS reminders</label>
        <label>Recipient timezone
          <select value={policy.timeZone || ""} onChange={event => setPolicy({ ...policy, timeZone: event.target.value || null })}>
            <option value="">Use company business timezone</option>
            {["Sydney", "Melbourne", "Brisbane", "Adelaide", "Darwin", "Perth", "Hobart", "Broken_Hill", "Lord_Howe", "Eucla"].map(zone =>
              <option key={zone} value={`Australia/${zone}`}>{zone.replaceAll("_", " ")}</option>)}
          </select>
        </label>
        <p>Use the invoice editor to change the billing contact or mobile. Saving these controls does not confirm a message.</p>
        <Button variant="secondary" type="button" onClick={() => onSave(policy)}>Save reminder controls</Button>
      </fieldset>
    </details>
  );
}

export default function SmsReviewPage() {
  const [data, setData] = useState<SmsReviewList | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showAll, setShowAll] = useState(false);
  async function load() {
    setData(await smsReviewService.list());
  }
  async function run(action: () => Promise<unknown>, success?: string) {
    setBusy(true); setError(""); setNotice("");
    try {
      await action(); await load();
      if (success) setNotice(success);
    } catch (err) {
      setError(err instanceof Error ? err.message : "SMS review is unavailable.");
      // A failed or stale confirmation must show refreshed data, never silently
      // retry the action or substitute a new preview hash.
      try { await load(); } catch { /* Keep the error and last recorded view. */ }
    } finally { setBusy(false); }
  }
  useEffect(() => { void run(async () => {}); }, []);
  const items = data?.items.filter(item => showAll || pendingStates.includes(item.status)) || [];
  return (
    <main className="sms-review-page">
      <header className="sms-review-heading">
        <div><Link to="/">← PMapp</Link><p className="sms-review-eyebrow">SERVICE NOTIFICATIONS</p>
          <h1>Review SMS notifications</h1>
          <p>Confirm or skip each message after checking its recipient and final text.</p></div>
        <div className="sms-review-toolbar">
          <Button variant="secondary" disabled={busy} onClick={() => void run(async () => {})}>Refresh</Button>
          <Button variant="secondary" disabled={busy} onClick={() => void run(() => smsReviewService.scan(), "Reminder scan complete. No messages were sent.")}>Prepare reminders</Button>
        </div>
      </header>
      {data && <div className="sms-review-status" role="status">
        {data.provider === "disabled" ? <strong>SMS sending is disabled.</strong> : <strong>SMS provider: Mobile Message.</strong>}
        <span>Both quotation and invoice messages require an individual confirmation. Preparing reminders sends nothing.</span>
        <small>{data.asOf} · {data.timeZone}</small>
      </div>}
      {error && <p className="sms-review-error" role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      <div className="sms-review-list-heading">
        <h2>{showAll ? "Recorded notifications" : "Awaiting review"} <span>{items.length}</span></h2>
        <label><input type="checkbox" checked={showAll} onChange={event => setShowAll(event.target.checked)} />Include processed records</label>
      </div>
      {busy && !data && <p role="status">Loading reminders…</p>}
      {!busy && data && items.length === 0 && <Card><p>No reminders to review. Prepare reminders to scan current business records; historical overdue stages are not bulk imported.</p></Card>}
      <div className="sms-review-list">
        {items.map(item => {
          const snapshot = item.snapshot;
          const pending = pendingStates.includes(item.status);
          const reason = item.reason || snapshot.blockReason;
          return <Card key={item.id} className="sms-review-card">
            <div className="sms-review-card-heading"><div>
              <p className="sms-review-eyebrow">{snapshot.kind === "invoice" ? `INVOICE · ${snapshot.stage}-DAY STAGE` : "QUOTATION SIGNED"}</p>
              <h3>{snapshot.reference}</h3></div><span className="sms-review-badge">{item.status}</span></div>
            <div className="sms-review-card-grid">
              <div><dl>
                <div><dt>Company</dt><dd>{snapshot.company === "equilibrium" ? "Equilibrium" : "Linfinity"}</dd></div>
                <div><dt>Client / project</dt><dd>{snapshot.client || "—"}{snapshot.project ? ` · ${snapshot.project}` : ""}</dd></div>
                <div><dt>Recipient</dt><dd>{snapshot.recipientName || "Not resolved"}<br />{snapshot.recipientPhone || "No valid notification mobile"}</dd></div>
                {snapshot.kind === "invoice" && <>
                  <div><dt>Due date / overdue</dt><dd>{snapshot.dueDate} · {snapshot.overdueDays} days</dd></div>
                  <div><dt>{pending ? "Outstanding balance" : "Recorded balance"}</dt><dd>{new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(snapshot.balance || 0)}</dd></div>
                </>}
                <div><dt>Sending time</dt><dd>{snapshot.timeZone}<br /><small>{snapshot.timeZoneRule}</small></dd></div>
              </dl></div>
              <div className="sms-review-preview"><h4>{pending ? "Final SMS preview" : "Recorded SMS preview"}</h4>
                <pre>{snapshot.body || "No sendable single-segment template is available."}</pre>
                <p>Source line included · maximum one SMS segment · no links</p></div>
            </div>
            {reason && <p className="sms-review-reason">{reasonText[reason] || reason.replaceAll("_", " ")}</p>}
            {terminalDescriptions[item.status] && <p>{terminalDescriptions[item.status]}</p>}
            {item.providerMessageId && <p className="sms-review-provider-id">Provider message ID: {item.providerMessageId}</p>}
            {pending && snapshot.kind === "invoice" && !snapshot.blockReason && !item.canConfirm && <p className="sms-review-reason">Confirm during the recipient's 09:00–17:00 sending window. This reminder will not send automatically later.</p>}
            <div className="sms-review-actions">
              {item.canSkip && <Button variant="ghost" disabled={busy} onClick={() => void run(() => smsReviewService.act(item.id, "skip", item.previewHash), "Reminder skipped.")}>Skip this reminder</Button>}
              {pending && <Button disabled={busy || !item.canConfirm} onClick={() => void run(async () => {
                const result = await smsReviewService.act(item.id, "confirm", item.previewHash);
                setNotice(result.outcome === "disabled" ? "Confirmation recorded with sending disabled. No SMS was sent." : "Result recorded. Accepted does not mean delivered.");
              })}>{data?.provider === "disabled" ? "Confirm this SMS · sending disabled" : "Confirm this SMS"}</Button>}
            </div>
            {snapshot.kind === "invoice" && <InvoiceControls key={item.id + ":" + item.previewHash + ":" + JSON.stringify(item.invoicePolicy)}
              item={item} busy={busy} onSave={policy => void run(() => smsReviewService.policy(item.id, policy), "Reminder controls saved. Review the refreshed message separately.")} />}
          </Card>;
        })}
      </div>
      {data && data.items.length >= data.limit && <p>The latest {data.limit} records are shown.</p>}
    </main>
  );
}
